import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as NextArrow } from '../../assets/icons/next-arrow.svg';
import { ReactComponent as PreviousArrow } from '../../assets/icons/prev-arrow.svg';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { ApiRequest } from '../../interface/common.interface';
import SelectFiled from '../select-field/SelectField';

interface PaginationProperties {
  active: number;
  setActive: (value: ApiRequest) => void;
  size: number;
  limit?: number;
  totalResults?: number;
  loading?: boolean;
}

/**
 * Pagination function component
 * @param {any} param0 - The param0
 * @param {any} param0.active - The active prop
 * @param {any} param0.size - The size prop
 * @param {any} param0.setActive - The setActive prop
 * @param {any} param0.limit - The limit prop
 * @param {any} param0.totalResults - The totalResults prop
 * @param {any} param0.loading - The loading prop
 */
function Pagination({
  active,
  size,
  setActive,
  limit = 0,
  totalResults = 0,
  loading,
}: PaginationProperties) {
  const { t, i18n } = useTranslation('sidePanel');
  const step: number = 1;
  const showingNumbers = 3; // step * 2 + 1
  const startNumber: number = 2;
  const [startArrayNumber, setStartArrayNumber] = useState<number>(step);
  const [needStartDots, setNeedStartDots] = useState<boolean>(false);
  const [needEndDots, setNeedEndDots] = useState<boolean>(false);

  /**
   * Handle the page change function
   * @param {any} pageNumber page number
   */
  const handlePageChange = (pageNumber: number) => {
    setActive({
      page: pageNumber,
      limit,
    });
  };

  /**
   * Change the page limit function
   * @param {any} name field name
   * @param {any} value field value
   */
  const handleChange = (name: string, value: number) => {
    setActive({
      page: DefaultApiParameters.page,
      limit: value,
    });
  };

  /**
   * Handle the arrow click the function
   * @param {any} pageNumber page number
   * @param {any} type arrow type
   */
  const handleArrowClick = (pageNumber: number, type: 'prev' | 'next') => {
    if (type === 'prev' && active > 1) {
      setActive({
        page: pageNumber,
        limit,
      });
    } else if (type === 'next' && active < size) {
      setActive({
        page: pageNumber,
        limit,
      });
    }
  };

  type DropdownOptionInfo = {
    label: string;
    value: string | number;
  };

  const dropDownOption: DropdownOptionInfo[] = [
    {
      label: '20',
      value: 20,
    },
    {
      label: '50',
      value: 50,
    },
    {
      label: '100',
      value: 100,
    },
  ];

  /**
   * Function to dispatch custom input from select value
   * @param {any} value selected value
   */
  const handleCustomizedInput = (value: string | number) => {
    setActive({
      page: DefaultApiParameters.page,
      limit: Number(value),
    });
  };

  useEffect(() => {
    if (active > step) {
      setStartArrayNumber(active - step);
      setNeedStartDots(active > step + startNumber);
    } else {
      setStartArrayNumber(step);
      setNeedStartDots(false);
    }

    if (size > showingNumbers) {
      setNeedEndDots(size > active + step + 1);

      if (size < active + step + 1) {
        setStartArrayNumber(size - showingNumbers);
      }
    }
  }, [active, step, size, startNumber, showingNumbers]);

  return (
    <div className="pagination-wrap" dir={i18n.dir()}>
      <div className="limit-container">
        <span>{t('itemsPerPage')}</span>
        <div className="limit-select-box">
          <SelectFiled
            inputLabel=""
            name="dropDown"
            selectedValue={limit}
            onChangeValue={handleChange}
            options={dropDownOption}
            allowCustomInput
            handleCustomizedInput={handleCustomizedInput}
            loading={loading}
          />
        </div>
        <span>
          {t('of')} {totalResults}
        </span>
      </div>
      <ul className="pagination-container">
        <li
          className={`pagination-arrow ${active > 1 ? 'active-arrow' : 'disable-arrow'}`}
          role="presentation"
          onClick={() => handleArrowClick(active - 1, 'prev')}>
          <PreviousArrow />
        </li>

        {size > showingNumbers + startNumber ? (
          <>
            <li
              role="presentation"
              onClick={(event) => handlePageChange(Number(event.currentTarget.textContent))}
              className={`pagination-item ${active === 1 ? 'active-item' : ''}`}>
              1
            </li>

            {needStartDots && (
              <div className="pagination-dots">
                <span>...</span>
              </div>
            )}

            {Array.from({ length: showingNumbers }, (_, index) => {
              const contentNumber = needStartDots ? startArrayNumber + index : startNumber + index;
              return (
                <li
                  key={index}
                  className={`pagination-item ${active === contentNumber ? 'active-item' : ''}`}
                  role="presentation"
                  onClick={() => handlePageChange(contentNumber)}>
                  {contentNumber}
                </li>
              );
            })}

            {needEndDots && (
              <div className="pagination-dots">
                <span>...</span>
              </div>
            )}

            <li
              className={`pagination-item ${active === size ? 'active-item' : ''}`}
              role="presentation"
              onClick={() => handlePageChange(size)}>
              {size}
            </li>
          </>
        ) : (
          Array.from({ length: size }, (_, index) => (
            <li
              key={index}
              className={`pagination-item ${active === index + 1 ? 'active-item' : ''}`}
              role="presentation"
              onClick={() => handlePageChange(index + 1)}>
              {index + 1}
            </li>
          ))
        )}

        <li
          className={`pagination-arrow ${active < size ? 'active-arrow' : 'disable-arrow'}`}
          role="presentation"
          onClick={() => handleArrowClick(active + 1, 'next')}>
          <NextArrow />
        </li>
      </ul>
    </div>
  );
}

Pagination.defaultProps = {
  limit: 0,
  totalResults: 0,
  loading: false,
};

export default Pagination;
